import React from "react";
import { ArrowRight, Star, Clock, MapPin } from "lucide-react";
import { ORDER_URL as DEFAULT_ORDER_URL, HERO_IMAGE as DEFAULT_HERO_IMAGE } from "../data/site";
import { useSiteContent } from "../data/SiteContentContext";

const ICONS = [Star, Clock, MapPin];

export default function Hero() {
  const { content } = useSiteContent();
  const h = content?.hero || {};
  const ORDER_URL = content?.links?.order_url || DEFAULT_ORDER_URL;
  const HERO_IMAGE = h.image || DEFAULT_HERO_IMAGE;
  const line1 = h.headline_line1 || "BOLD FLAVOR.";
  const line2 = h.headline_line2 || "MADE FRESH.";
  const line3 = h.headline_line3 || "BUILT FOR YOU.";
  const subtext = h.subtext || "Fast. Fresh. Made to crave.\nBurgers. Tacos. Wings. Seafood.";
  const badges = (h.badges && h.badges.length ? h.badges : [
    { title: "TOP RATED", sub: "in Queens" },
    { title: "READY IN", sub: "10–15 min" },
    { title: "JAMAICA", sub: "Queens, NY" },
  ]).slice(0, 3);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-[#0B0B0B] lg:min-h-[100vh]"
    >
      {/* Faint mural backdrop — shop's "Welcome to #BTW" wall art, low opacity */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: "url('/brand/btw-mural.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          opacity: 0.32,
          mixBlendMode: "screen",
        }}
      />
      {/* Tint to keep text legible */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-b from-[#0B0B0B]/55 via-[#0B0B0B]/45 to-[#0B0B0B]/80" />

      {/* DESKTOP hero food image (right side) */}
      <div className="hidden lg:block absolute inset-0 z-[1]">
        <img
          src={HERO_IMAGE}
          alt="BTW signature combo - burgers, tacos, wings"
          className="h-full w-full object-cover object-right opacity-90"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B] via-[#0B0B0B]/85 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B]/60 via-transparent to-[#0B0B0B]/40" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 pt-28 sm:pt-32 lg:pt-40 pb-12 lg:pb-28">
        {/* Mascot — positioned top-right on mobile/tablet, hidden on desktop where bg image takes over */}
        <img
          src="/brand/btw-mascot.png"
          alt="BTW mascot"
          data-testid="hero-mascot"
          className="lg:hidden absolute right-2 sm:right-6 top-20 sm:top-24 w-32 sm:w-44 h-auto select-none drop-shadow-[0_10px_25px_rgba(95,189,214,0.25)] pointer-events-none"
          draggable={false}
        />

        <div className="max-w-2xl">
          <h1 className="font-display text-white uppercase leading-[0.95] text-5xl sm:text-7xl lg:text-[88px] tracking-tight">
            {line1}
            <br />
            <span style={{ color: "#5FBDD6" }}>{line2}</span>
            <br />
            {line3}
          </h1>

          <p className="mt-6 text-white/90 font-medium text-lg sm:text-xl max-w-md whitespace-pre-line">
            {subtext}
          </p>

          <div className="mt-8 flex flex-nowrap items-stretch gap-2 sm:gap-3">
            <a
              href={ORDER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-order-now-btn"
              className="group flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full bg-[#A3D900] hover:bg-[#8ebf00] text-[#0B0B0B] font-extrabold tracking-[0.12em] sm:tracking-[0.16em] uppercase px-3 sm:px-5 lg:px-6 py-2.5 sm:py-3 text-[10px] sm:text-xs whitespace-nowrap transition-all duration-200 shadow-[0_8px_30px_rgba(163,217,0,0.25)]"
            >
              Order Now
              <ArrowRight
                size={12}
                className="hidden sm:inline transition-transform group-hover:translate-x-1"
              />
            </a>
            <a
              href="/menu"
              data-testid="hero-view-menu-btn"
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-white text-white hover:bg-white hover:text-[#0B0B0B] font-extrabold tracking-[0.12em] sm:tracking-[0.16em] uppercase px-3 sm:px-5 lg:px-6 py-2.5 sm:py-3 text-[10px] sm:text-xs whitespace-nowrap transition-all duration-200"
            >
              View Menu
            </a>
            <a
              href="#catering"
              data-testid="hero-jump-catering-btn"
              className="group flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full bg-[#5FBDD6] hover:bg-[#3FA8C4] text-[#0B0B0B] font-extrabold tracking-[0.12em] sm:tracking-[0.16em] uppercase px-3 sm:px-5 lg:px-6 py-2.5 sm:py-3 text-[10px] sm:text-xs whitespace-nowrap transition-all duration-200 shadow-[0_8px_30px_rgba(95,189,214,0.25)]"
            >
              <span className="sm:hidden">Catering</span>
              <span className="hidden sm:inline">Catering Options</span>
              <ArrowRight
                size={12}
                className="hidden sm:inline transition-transform group-hover:translate-x-1"
              />
            </a>
          </div>
        </div>

        {/* MOBILE/TABLET food image (visible below CTAs, hidden on desktop where bg covers) */}
        <div className="lg:hidden mt-10 -mx-5 sm:-mx-8" data-testid="hero-mobile-image">
          <div className="relative aspect-[16/10] overflow-hidden">
            <img
              src={HERO_IMAGE}
              alt="BTW signature combo"
              className="absolute inset-0 h-full w-full object-cover object-center"
              loading="eager"
            />
            <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#0B0B0B] to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0B0B0B] to-transparent" />
          </div>
        </div>

        {/* Trust badges */}
        <div className="mt-8 sm:mt-10 grid grid-cols-3 gap-2 sm:gap-5 max-w-xl relative z-10">
          {badges.map((b, i) => {
            const Icon = ICONS[i] || Star;
            return (
              <Badge key={`${b.title}-${i}`} icon={<Icon size={18} />} title={b.title} sub={b.sub} />
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Badge({ icon, title, sub }) {
  return (
    <div className="flex flex-col items-center sm:flex-row sm:items-center text-center sm:text-left gap-2 sm:gap-3" data-testid={`hero-badge-${(title || "").toLowerCase().replace(/\s+/g, "-")}`}>
      <span
        className="inline-flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full"
        style={{ background: "rgba(163,217,0,0.15)", color: "#A3D900" }}
      >
        {icon}
      </span>
      <div className="leading-tight">
        <div className="font-bold text-white text-[11px] sm:text-sm tracking-wider uppercase">
          {title}
        </div>
        <div className="text-white/70 text-[10px] sm:text-sm">{sub}</div>
      </div>
    </div>
  );
}
